import { Card, CardContent, Tooltip } from "@altimateai/lego";
import { Button, Badge } from "@altimateai/lego";
import styles from "./styles.module.scss";
import { Paginate, PaginateProps } from "../../components/paginate";
import { CollapsibleField } from "../../components/text";
import { default as DBTIcon } from "../../assets/icons/dbt.svg?react";
import classNames from "classnames";
import dayjs from "dayjs";
import { Stack } from "@altimateai/lego";
import { useAppState } from "@/modules/app/useAppContext";
import { DbtModel } from "@/types/dbtModels";

interface TableListProps {
  tables?: DbtModel[];
  onItemClick: (table: DbtModel) => void;
  paginate: PaginateProps;
  onDetailsClick?: (table: DbtModel) => void;
  onViewSchema: (table: DbtModel) => void;
  errorForRk?: string;
  modelError?: string | null;
  connectionNameMap?: Record<string, string>;
}

const TableList = ({
  tables = [],
  onItemClick,
  paginate,
  onDetailsClick,
  onViewSchema,
  errorForRk = "",
  modelError = "",
  connectionNameMap = {},
}: TableListProps) => {
  const { staleResourcesEnabled, enableCloudIntegration } = useAppState();
  return (
    <div className={styles.table_list} aria-label="Dbt models list">
      <Card>
        <CardContent className="al-pt-4">
          <Stack justify="between" align="center">
            <div className="fs-5 text-muted">{paginate.itemCount} results</div>
          </Stack>
          {tables.map(table => (
            <div
              key={table.rk}
              className={styles.card}
              aria-label={`dbt model ${table.model_name}`}
              role="button"
              tabIndex={0}
              onClick={e => {
                e.stopPropagation();
                onItemClick(table);
              }}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  onItemClick(table);
                }
              }}
            >
              <Stack align={"center"} className="mb-2">
                <div className="me-2 icon-md">
                  <DBTIcon className="al-h-4 al-w-4 al-mt-1" />
                </div>
                <div className="text-primary fs-5 text-overflow">{table.model_name}</div>
                {table.is_deleted && (
                  <Tooltip
                    title={`Deleted from manifest on ${dayjs(table.deleted_at).format("DD MMM YYYY, hh:mm A")}`}
                  >
                    <Badge variant="destructive" className="al-font-semibold">
                      Deleted
                    </Badge>
                  </Tooltip>
                )}
                {staleResourcesEnabled && table.is_stale && !table.is_deleted && (
                  <Tooltip
                    title={`Last seen at ${dayjs(table.last_seen).format("DD MMM YYYY, hh:mm A")}`}
                  >
                    <Badge
                      variant="warning"
                      className="al-font-semibold al-bg-yellow-100 al-text-yellow-800 al-border-yellow-200"
                    >
                      Stale
                    </Badge>
                  </Tooltip>
                )}
              </Stack>
              <Stack align="center" className="mt-2 mb-2">
                <div className="me-2 text-overflow">
                  {table.database}.{table.model_schema}/{table.alias}
                </div>
              </Stack>
              {enableCloudIntegration &&
                table.dbt_core_integration_id != null &&
                connectionNameMap[String(table.dbt_core_integration_id)] && (
                  <Stack align="center" className="mb-2">
                    <div className="me-2">Connection:</div>
                    <div className="text-muted">
                      {connectionNameMap[String(table.dbt_core_integration_id)]}
                    </div>
                  </Stack>
                )}
              {table.is_deleted && table.deleted_at && (
                <div className="text-danger fs-6 mb-2">
                  Deleted from manifest on {dayjs(table.deleted_at).format("DD MMM YYYY, hh:mm A")}
                </div>
              )}
              {!table.is_deleted && table.is_stale && table.last_seen && (
                <div className="text-muted fs-6 mb-2">
                  Last seen {dayjs(table.last_seen).format("DD MMM YYYY, hh:mm A")}
                </div>
              )}
              <div className={classNames(styles.grid_3, "mb-2")}>
                <Stack>
                  <div className="me-2">Project:</div>
                  <div className="text-muted">{table.project_name}</div>
                </Stack>
                <Stack>
                  <div className="me-2">Resource Type:</div>
                  <div className="text-muted">{table.resource_type}</div>
                </Stack>
                <Stack>
                  <div className="me-2">Materialization:</div>
                  <div className="text-muted">{table.materialization}</div>
                </Stack>
              </div>
              <div className={classNames(styles.grid_3, "mb-2")}>
                <Stack>
                  <div className="me-2">Database:</div>
                  <div className="text-muted">{table.database}</div>
                </Stack>
                <Stack>
                  <div className="me-2">Schema:</div>
                  <div className="text-muted">{table.model_schema}</div>
                </Stack>
                <Stack>
                  <div className="me-2">Total Columns:</div>
                  <div className="text-muted">{table.columns.length}</div>
                </Stack>
              </div>
              {table.description && (
                <CollapsibleField label="Description" text={table.description} defaultLines={3} />
              )}
              {/* {table.tags.length > 0 && (
              <div className="mt-2 al-flex gap-sm">
                <div className="me-2">Tags:</div>
                <div className="al-flex flex-wrap gap-sm">
                  <ColorInsightTags tags={table.tags} />
                </div>
              </div>
            )} */}
              <Stack justify="between" align="center" className="mt-2">
                {errorForRk && modelError && errorForRk === table?.rk ? (
                  <div className="text-danger">{modelError}</div>
                ) : (
                  <div />
                )}
                <Stack gap="sm" align="center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation();
                      onViewSchema(table);
                    }}
                    aria-label={`View ${table.model_name} Schema`}
                  >
                    View Schema
                  </Button>
                  {onDetailsClick && (
                    <Button
                      size="sm"
                      onClick={e => {
                        e.stopPropagation();
                        onDetailsClick(table);
                      }}
                      aria-label={`View ${table.model_name} Details`}
                    >
                      View Details
                    </Button>
                  )}
                </Stack>
              </Stack>
            </div>
          ))}
          <Paginate {...paginate} />
        </CardContent>
      </Card>
    </div>
  );
};

export { TableList };
